import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { assetsActions } from "features/assets/model/slice/assetsSlice"
import { NOTIFICATION_TYPES, showNotification } from "features/common/notifications"
import { fetchFileDataThunk, filesPreviewActions } from "features/files-preview/model/slice/filesPreviewSlice"
import _ from "lodash"
import { assetsApi } from "shared/api/assetsApi"
import { boardsApi } from "shared/api/boardsApi"
import { request } from "shared/api/request"
import { RootState } from "shared/config/store"
import { LOADING_STATUSES } from "shared/constants/loadingStatuses"
import { getFilesSdk } from "shared/sdk/files"

const initialState = {
  nodeId: null,
  versions: [],
  selectedVersion: null,
  status: LOADING_STATUSES.LOADING
}

export const fetchFileVersionsThunk = createAsyncThunk(
  'fileVersionsSlice/fetchFileVersionsThunk',
  async ({ nodeId } : { nodeId: string }) => {

    const sdk = getFilesSdk()
    const { fileFamilyVersionsService: fileVersionsService } = sdk
    const response = await fileVersionsService.getVersions(nodeId, { limit: 1000, preview: true, preview_jwt: true } as any)

    return response.data._embedded.versions
  }
)

export const fetchSharedFileVersionsThunk = createAsyncThunk(
  'fileVersionsSlice/fetchSharedFileVersionsThunk',
  async ({ nodeId } : { nodeId: string }, { getState }) => {
    const state = getState() as RootState
    const userId = state.user.user?.id ?? 0
    const sdk = getFilesSdk()
    const {  publicLinksService } = sdk

    const url = `/api/2/users/${userId}/shared_with_me/fs/${state.publicLinkFiles?.nodeData?.id}/nodes/${nodeId}`
    const rsp = await request('GET', url) as any

    const {
      _links: {
        versions: { href: fileVersionsUrlTmpl },
      },
    } = rsp

    const params = { embedded: true, limit: 1000, preview: true, preview_jwt: true }

    const response = await publicLinksService.getSharedWithMeFileVersionsByLink(fileVersionsUrlTmpl, nodeId, params)

    return response.data._embedded.versions
  }
)

export const restoreFileVersionThunk = createAsyncThunk(
  'fileVersionsSlice/restoreFileVersionThunk',
  async ({ currentItemId, versionId, callback } : { currentItemId: string, versionId: string, callback?: () => void }, { dispatch }) => {
    const sdk = getFilesSdk()
    const { familyFolderOperationsService: folderOperationsService } = sdk
    try {
      const response = await folderOperationsService.changeCurrentVersionByVersionId(currentItemId, versionId)

      dispatch(fetchFileVersionsThunk({ nodeId: currentItemId }))

      dispatch(filesPreviewActions.updateItem({ ...response.data, id: currentItemId }))
      dispatch(assetsActions.updateAsset({ ...response.data, id: currentItemId }))

      dispatch(fetchFileDataThunk({ node: { id: currentItemId }, isPublicLink: false }))

      if (callback) {
        callback()
      }
    } catch (error) {
    } finally {
      dispatch(actions.resetState())
    }
  }
)

export const restoreSharedFileVersionThunk = createAsyncThunk(
  'fileVersionsSlice/restoreSharedFileVersionThunk',
  async ({ currentItemId, versionId, callback } : { currentItemId: string, versionId: string, callback?: () => void }, { dispatch, getState }) => {
    const state = getState() as RootState
    const userId = state.user.user?.id ?? 0
    const sharedId = state.publicLinkFiles?.nodeData?.id
    const sdk = getFilesSdk()
    const { familyFolderOperationsService: folderOperationsService } = sdk

    try {
      const response = await folderOperationsService.changeSharedCurrentVersionByVersionId(currentItemId, versionId, sharedId, userId)

      dispatch(fetchSharedFileVersionsThunk({ nodeId: currentItemId }))

      dispatch(filesPreviewActions.updateItem({ ...response.data, id: currentItemId }))
      dispatch(assetsActions.updateAsset({ ...response.data, id: currentItemId }))

      dispatch(fetchFileDataThunk({ node: { id: currentItemId }, isPublicLink: true }))

      if (callback) {
        callback()
      }
    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Download access denied',
        message: 'Shared link is not available for download',
      })
    } finally {
      dispatch(actions.resetState())
    }
  }
)

export const uploadNewFileVersionFromExistedFileThunk = createAsyncThunk(
  'fileVersionsSlice/uploadNewFileVersionFromExistedFileThunk',
  async ({ nodeId, uploadedFileId, callback } : { nodeId: string, uploadedFileId: string, callback?: () => void }, { getState, dispatch }) => {
    const state = getState() as RootState
    const userId = state.user.user?.family_user_id

    const { _links: { node } } = await boardsApi.getFsRoot(userId)
    const urlTemplate = _.template(node.href, { interpolate: /\{(.+?)\}/g })
    const url = urlTemplate({ node_id: uploadedFileId })

    const nodeData = await request('GET', url, {}, { host: null })

    const response = await assetsApi.uploadNewFileVersionFromExistedFile(userId, nodeId, { file_source: nodeData?._links?.self?.href })

    dispatch(fetchFileVersionsThunk({ nodeId }))

    dispatch(filesPreviewActions.updateItem({ ...response, id: nodeId }))
    dispatch(assetsActions.updateAsset({ ...response, id: nodeId }))

    dispatch(fetchFileDataThunk({ node: { id: nodeId }, isPublicLink: false }))

    callback?.()
  }
)

export const fileVersionsSlice = createSlice({
  name: 'fileVersions',
  initialState,
  reducers: {
    setNodeId: (state, action) => {
      state.nodeId = action.payload
    },
    setSelectedVerion: (state, action) => {
      state.selectedVersion = action.payload
    },
    resetState: (state) => {
      state.nodeId = null
      state.versions = []
      state.status = LOADING_STATUSES.LOADING,
      state.selectedVersion = null
    }
  },
  extraReducers(builder) {
    builder
      .addCase(fetchFileVersionsThunk.pending, (state) => {
        state.status = LOADING_STATUSES.LOADING
      })
      .addCase(fetchFileVersionsThunk.fulfilled, (state, action) => {
        state.status = LOADING_STATUSES.SUCCEEDED
        state.versions = action.payload
        state.selectedVersion = action.payload[0]?.id
      })
      .addCase(fetchFileVersionsThunk.rejected, (state) => {
        state.status = LOADING_STATUSES.FAILED
      })

      .addCase(fetchSharedFileVersionsThunk.pending, (state) => {
        state.status = LOADING_STATUSES.LOADING
      })
      .addCase(fetchSharedFileVersionsThunk.fulfilled, (state, action) => {
        state.status = LOADING_STATUSES.SUCCEEDED
        state.versions = action.payload
        state.selectedVersion = action.payload[0]?.id
      })
      .addCase(fetchSharedFileVersionsThunk.rejected, (state) => {
        state.status = LOADING_STATUSES.FAILED
      })
  },
})

export default fileVersionsSlice.reducer

const {
  reducer, actions
} = fileVersionsSlice

export { reducer as fileVersionsReducer, actions as fileVersionsActions }
